import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Select, DatePicker, Button, Table, Tabs, Tooltip, message, Spin, Modal } from 'antd';
import moment from 'moment';
import { getClients, getClientDepartments, getEmployeeTimesheetOverview, getEmployeeAssignments, getEmployees, sendTimesheetPdf, bulkSendTimesheetPdfs, sendTimesheetWhatsapp, bulkSendTimesheetWhatsapp, previewTimesheetPdf, previewAssignmentPdf, sendAssignmentEmail, bulkSendAssignmentEmails } from '../api';
import { FaEnvelope, FaWhatsapp } from 'react-icons/fa';

const { Option } = Select;
const { TabPane } = Tabs;

const Notifications = () => {
  const [selectedTab, setSelectedTab] = useState('timesheet'); // 'timesheet' | 'assignment'
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [sendingRow, setSendingRow] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    getClients().then(res => {
      if (res.success) setClients(res.data);
    });
    getEmployees().then(res => {
      if (res.success) setEmployees(res.data);
    });
  }, []);

  useEffect(() => {
    if (!selectedClient) {
      setDepartments([]);
      setSelectedDepartment(null);
      return;
    }
    getClientDepartments(selectedClient).then(res => {
      if (res.success) setDepartments(res.data);
    });
  }, [selectedClient]);

  const fetchTimesheetRows = async () => {
    if (!selectedClient && !selectedEmployee) return;
    setLoading(true);
    try {
      if (selectedEmployee) {
        // Fetch all assignments overlapping the month, then filter by employee
        const res = await getEmployeeAssignments({ start_date: selectedMonth.format('YYYY-MM-01') });
        const list = Array.isArray(res?.data) ? res.data : [];
        const filtered = list.filter(a => String((a.employee?.id || a.employee_details?.id)) === String(selectedEmployee));
        const tableData = filtered.map(a => ({
          key: a.id,
          id: a.id,
          employee_id: (a.employee?.id || a.employee_details?.id),
          emp_code: (a.employee?.emp_code || a.employee_details?.emp_code || ''),
          name: (a.employee?.name || a.employee_details?.name || ''),
          client_name: a.client?.name || '',
          department_name: a.client_department?.name || '',
          total_hours: '', // unknown here; backend will aggregate when sending
        }));
        setRows(tableData);
      } else {
        const res = await getEmployeeTimesheetOverview({
          client_id: selectedClient,
          client_department_id: selectedDepartment,
          month: selectedMonth.format('YYYY-MM'),
        });
        const assignments = Array.isArray(res?.data.data?.assignments) ? res.data.data.assignments : [];
        const timesheets = Array.isArray(res?.data.data?.timesheets) ? res.data.data.timesheets : [];
        const tableData = assignments.map(a => {
          const tsList = timesheets.filter(ts => ts.employee_assignment_id === a.id);
          const total_hours = tsList.reduce((sum, ts) => sum + Number(ts.hours_worked || 0), 0);
          return {
            key: a.id,
            id: a.id,
            employee_id: a.employee_details?.id,
            emp_code: a.employee_details?.emp_code || '',
            name: a.employee_details?.name || '',
            client_name: a.client?.name || '',
            department_name: a.client_department?.name || '',
            total_hours,
          };
        });
        setRows(tableData);
      }
    } catch (e) {
      message.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedClient || selectedEmployee) fetchTimesheetRows();
  }, [selectedClient, selectedDepartment, selectedMonth, selectedEmployee]);

  const filteredRows = useMemo(() => {
    if (!selectedEmployee) return rows;
    return rows.filter(r => String(r.employee_id) === String(selectedEmployee));
  }, [rows, selectedEmployee]);

  const columns = useMemo(() => [
    { title: 'Employee Code', dataIndex: 'emp_code', key: 'emp_code', width: 140 },
    { title: 'Name', dataIndex: 'name', key: 'name', width: 200 },
    { title: 'Client', dataIndex: 'client_name', key: 'client_name', width: 200 },
    { title: 'Department', dataIndex: 'department_name', key: 'department_name', width: 180 },
    selectedTab === 'timesheet' ? { title: 'Total Hours', dataIndex: 'total_hours', key: 'total_hours', width: 120 } : null,
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 260,
      render: (_, record) => (
        <>
          <Button size="small" style={{ marginRight: 8 }} onClick={() => handlePreview(record)}>Preview</Button>
          {selectedTab === 'assignment' ? (
            <Tooltip title="Send Email">
              <Button size="small" style={{ marginRight: 8 }} loading={sendingRow === record.id}
                onClick={() => handleSendAssignmentEmail(record)}>
                <FaEnvelope style={{ color: '#1890ff', fontSize: 16 }} />
              </Button>
            </Tooltip>
          ) : (
          <Tooltip title="Send Email">
            <Button size="small" style={{ marginRight: 8 }} loading={sendingRow === record.id}
              onClick={() => handleSendSingle(record, 'email')}>
              <FaEnvelope style={{ color: '#1890ff', fontSize: 16 }} />
            </Button>
          </Tooltip>
          )}
          <Tooltip title="Send WhatsApp">
            <Button size="small" loading={sendingRow === record.id}
              onClick={() => handleSendSingle(record, 'whatsapp')}>
              <FaWhatsapp style={{ color: '#25D366', fontSize: 18 }} />
            </Button>
          </Tooltip>
        </>
      )
    }
  ].filter(Boolean), [selectedTab, sendingRow]);
  const handlePreview = async (record) => {
    try {
      const month = selectedMonth.format('YYYY-MM');
      const blob = selectedTab === 'assignment'
        ? await previewAssignmentPdf(record.employee_id, month)
        : await previewTimesheetPdf(record.employee_id, month);
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (e) {
      message.error(e?.message || 'Preview failed');
    }
  };


  const handleSendSingle = async (record, channel) => {
    try {
      setSendingRow(record.id);
      const month = selectedMonth.format('YYYY-MM');
      if (selectedTab === 'timesheet') {
        if (channel === 'email') await sendTimesheetPdf(record.employee_id, month);
        else await sendTimesheetWhatsapp(record.employee_id, month);
        message.success(`Sent ${channel} to ${record.name}`);
      } else {
        message.warning('Assignment notifications not implemented yet');
      }
    } catch (e) {
      message.error(e?.message || 'Send failed');
    } finally {
      setSendingRow(null);
    }
  };

  const handleBulkSend = async (channel) => {
    if (!selectedClient) return;
    setBulkLoading(true);
    try {
      const month = selectedMonth.format('YYYY-MM');
      if (selectedTab === 'timesheet') {
        if (channel === 'email') await bulkSendTimesheetPdfs(month);
        else await bulkSendTimesheetWhatsapp(month);
        message.success('Bulk send triggered');
      } else {
        if (channel === 'email') await bulkSendAssignmentEmails(month);
        else message.warning('WhatsApp for assignment summary not supported yet');
        message.success('Bulk email triggered');
      }
    } catch (e) {
      message.error(e?.message || 'Bulk send failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleSendAssignmentEmail = async (record) => {
    try {
      setSendingRow(record.id);
      const month = selectedMonth.format('YYYY-MM');
      await sendAssignmentEmail(record.employee_id, month);
      message.success(`Assignment email sent to ${record.name}`);
    } catch (e) {
      message.error(e?.message || 'Send failed');
    } finally {
      setSendingRow(null);
    }
  };

  return (
    <div>
      <Tabs activeKey={selectedTab} onChange={setSelectedTab} style={{ marginBottom: 12 }}>
        <TabPane tab="Timesheet" key="timesheet" />
        <TabPane tab="Assignment" key="assignment" />
      </Tabs>

      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col>
          <Select allowClear placeholder="Select Client" style={{ width: 220 }} value={selectedClient}
            onChange={setSelectedClient}>
            {clients.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
          </Select>
        </Col>
        <Col>
          <Select allowClear placeholder="Select Department" style={{ width: 220 }} value={selectedDepartment}
            onChange={setSelectedDepartment} disabled={!selectedClient}>
            {departments.map(d => <Option key={d.id} value={d.id}>{d.name}</Option>)}
          </Select>
        </Col>
        <Col>
          <Select
            allowClear
            showSearch
            optionFilterProp="children"
            placeholder="Select Employee"
            style={{ width: 240 }}
            value={selectedEmployee}
            onChange={setSelectedEmployee}
          >
            {employees.map(e => (
              <Option key={e.id} value={e.id}>{e.emp_code ? `${e.emp_code} - ${e.name}` : e.name}</Option>
            ))}
          </Select>
        </Col>
        <Col>
          <DatePicker picker="month" value={selectedMonth} onChange={setSelectedMonth} style={{ width: 160 }} />
        </Col>
        <Col>
          <Button type="primary" onClick={fetchTimesheetRows} disabled={!selectedClient} loading={loading}>Refresh</Button>
        </Col>
        <Col>
          <Tooltip title="Send All (Email)">
            <Button onClick={() => handleBulkSend('email')} loading={bulkLoading} disabled={!selectedClient}>
              <FaEnvelope style={{ color: '#1890ff', fontSize: 16 }} />
            </Button>
          </Tooltip>
        </Col>
        <Col>
          <Tooltip title="Send All (WhatsApp)">
            <Button onClick={() => handleBulkSend('whatsapp')} loading={bulkLoading} disabled={!selectedClient}>
              <FaWhatsapp style={{ color: '#25D366', fontSize: 18 }} />
            </Button>
          </Tooltip>
        </Col>
      </Row>

      <Spin spinning={loading} tip="Loading...">
        <Table
          rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
          columns={columns}
          dataSource={filteredRows}
          rowKey={r => r.id}
          scroll={{ x: 1000 }}
          pagination={{ pageSize: 20 }}
        />
      </Spin>

      <Modal
        open={previewOpen}
        onCancel={() => { setPreviewOpen(false); if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(''); } }}
        footer={null}
        width={900}
        destroyOnClose
        title={`Preview - ${selectedMonth.format('YYYY-MM')}`}
      >
        {previewUrl ? (
          <iframe src={previewUrl} title="Timesheet Preview" style={{ width: '100%', height: '75vh', border: 'none' }} />
        ) : null}
      </Modal>
    </div>
  );
};

export default Notifications;


